// jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { driversAPI, bookingsAPI, transportsAPI } from '../../api/apiClient';
import { useTranslation } from 'react-i18next';
import '../../assets/css/AdminMapView.css';

const AdminMapView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState({
    bookings: [],
    transports: [],
    drivers: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, bookings, transports, drivers
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const googleMapsLoaded = useRef(false);

  // تحميل مكتبة Google Maps
  const loadGoogleMaps = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (window.google && window.google.maps) {
        googleMapsLoaded.current = true;
        resolve();
        return;
      }

      if (document.querySelector('script[src*="maps.googleapis.com"]')) {
        const checkLoaded = setInterval(() => {
          if (window.google && window.google.maps) {
            clearInterval(checkLoaded);
            googleMapsLoaded.current = true;
            resolve();
          }
        }, 100);
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyD1U0djKe-DQ9ldXwK8Ob-EiiHcxvRH_3Y&libraries=places`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        googleMapsLoaded.current = true;
        resolve();
      };
      
      script.onerror = () => {
        reject(new Error('Failed to load Google Maps'));
      };
      
      document.head.appendChild(script);
    });
  }, []);

  // تحميل جميع البيانات
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [bookingsRes, transportsRes, driversRes] = await Promise.all([
        bookingsAPI.all(),
        transportsAPI.all(),
        driversAPI.all()
      ]);

      const newData = {
        bookings: bookingsRes.success ? bookingsRes.data.data : [],
        transports: transportsRes.success ? transportsRes.data.data : [],
        drivers: driversRes.success ? driversRes.data.data : []
      };

      console.log('data', newData);

      setData(newData);
      
      if (googleMapsLoaded.current) {
        initializeMap(newData);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  // تهيئة الخريطة
  const initializeMap = useCallback((mapData) => {
    if (!window.google || !mapRef.current) {
      setError('Google Maps not available');
      return;
    }

    try {
      const mapOptions = {
        zoom: 7.4,
        center: new window.google.maps.LatLng(51.1657, 10.4515),
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'on' }]
          }
        ]
      };

      mapInstance.current = new window.google.maps.Map(mapRef.current, mapOptions);
      infoWindowRef.current = new window.google.maps.InfoWindow();
      
      markersRef.current = [];
      addMarkersToMap(mapData);
    } catch (err) {
      console.error('Error initializing map:', err);
      setError('Error initializing map');
    }
  }, []);

  // إضافة markers إلى الخريطة
  const addMarkersToMap = useCallback((mapData) => {
    const geocoder = new window.google.maps.Geocoder();
    
    // إضافة markers للطلبات
    mapData.bookings.forEach(booking => {
      addBookingMarker(booking, geocoder);
    });

    // إضافة markers للنقل
    mapData.transports.forEach(transport => {
      addTransportMarker(transport, geocoder);
    });

    // إضافة markers للسائقين
    mapData.drivers.forEach(driver => {
      addDriverMarker(driver);
    });
  }, []);

  // إضافة marker للطلب
  const addBookingMarker = useCallback((booking, geocoder) => {
    const address = Array.isArray(booking.booking_from) 
      ? booking.booking_from[0] 
      : booking.booking_from;

    if (!address) return;

    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        
        const marker = new window.google.maps.Marker({
          position: location,
          map: mapInstance.current,
          title: `Booking: ${booking.booking_id}`,
          icon: {
            url: 'https://img.icons8.com/arcade/35/box.png',
            scaledSize: new window.google.maps.Size(30, 30)
          },
          type: 'booking',
          data: booking
        });

        markersRef.current.push(marker);

        marker.addListener('click', () => {
          handleBookingClick(booking);
        });
      } else {
        console.warn(`Geocode error for booking ${booking.booking_id}:`, status);
      }
    });
  }, []);

  // إضافة marker للنقل
  const addTransportMarker = useCallback((transport, geocoder) => {
    const address = transport.booking_from;

    if (!address) return;

    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        
        const marker = new window.google.maps.Marker({
          position: location,
          map: mapInstance.current,
          title: `Transport: ${transport.booking_id}`,
          icon: {
            url: 'https://img.icons8.com/color/35/sofa.png',
            scaledSize: new window.google.maps.Size(30, 30)
          },
          type: 'transport',
          data: transport
        });

        markersRef.current.push(marker);

        marker.addListener('click', () => {
          handleTransportClick(transport);
        });
      } else {
        console.warn(`Geocode error for transport ${transport.booking_id}:`, status);
      }
    });
  }, []);

  // إضافة marker للسائق
  const addDriverMarker = useCallback((driver) => {
    if (!driver.latitute || !driver.longitute) {
      console.warn(`Missing coordinates for driver: ${driver.id}`);
      return;
    }

    try {
      const location = new window.google.maps.LatLng(
        parseFloat(driver.latitute),
        parseFloat(driver.longitute)
      );
      
      const marker = new window.google.maps.Marker({
        position: location,
        map: mapInstance.current,
        title: `Driver: ${driver.firstname} ${driver.lastname}`,
        icon: {
          url: 'https://img.icons8.com/color/35/000000/truck.png',
          scaledSize: new window.google.maps.Size(30, 30)
        },
        type: 'driver',
        data: driver
      });

      markersRef.current.push(marker);

      marker.addListener('click', () => {
        handleDriverClick(driver);
      });
    } catch (err) {
      console.error(`Error creating marker for driver ${driver.id}:`, err);
    }
  }, []);

  // التعامل مع النقر على الطلب
  const handleBookingClick = useCallback((booking) => {
    navigate(`/admin/bookings/details/${booking.id}`);
  }, [navigate]);

  // التعامل مع النقر على النقل
  const handleTransportClick = useCallback((transport) => {
    navigate(`/admin/transports/details/${transport.id}`);
  }, [navigate]);

  // التعامل مع النقر على السائق
  const handleDriverClick = useCallback((driver) => {
    navigate(`/admin/drivers/details/${driver.id}`);
  }, [navigate]);

  // تصفية الـ markers
  const filterMarkers = useCallback((filterType) => {
    markersRef.current.forEach(marker => {
      if (filterType === 'all') {
        marker.setVisible(true);
      } else {
        marker.setVisible(marker.type === filterType);
      }
    });
  }, []);

  // مسح جميع الـ markers
  const clearMarkers = useCallback(() => {
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
  }, []);

  // إعادة تحميل البيانات والخريطة
  const reloadData = useCallback(async () => {
    setError(null);
    clearMarkers();
    
    try {
      await loadGoogleMaps();
      await fetchAllData();
    } catch (err) {
      setError(err.message);
    }
  }, [loadGoogleMaps, fetchAllData, clearMarkers]);

  // إحصائيات البيانات
  const getStats = useCallback(() => ({
    bookings: data.bookings.length,
    transports: data.transports.length,
    drivers: data.drivers.length,
    total: data.bookings.length + data.transports.length + data.drivers.length
  }), [data]);

  // التهيئة الأولية
  useEffect(() => {
    const init = async () => {
      try {
        await loadGoogleMaps();
        await fetchAllData();
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    init();

    // Cleanup
    return () => {
      clearMarkers();
    };
  }, [loadGoogleMaps, fetchAllData, clearMarkers]);

  // عند تغيير الفلتر
  useEffect(() => {
    if (markersRef.current.length > 0) {
      filterMarkers(filter);
    }
  }, [filter, filterMarkers]);

  const stats = getStats();

  return (
    <div className="admin-map-view">
      <section className="content-header">
        <Container fluid>
          <Row>
            <Col>
              <div className="content-header-left">
                <h1>{t('title')}</h1>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="content">
        <Container fluid>
          {error && (
            <Alert variant="danger" className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <span>{error}</span>
                <button 
                  className="btn btn-sm btn-outline-danger"
                  onClick={reloadData}
                  disabled={loading}
                >
                  {t('retry')}
                </button>
              </div>
            </Alert>
          )}

          {/* إحصائيات وفلتر */}
          <Row className="mb-4">
            <Col lg={8}>
              <div className="stats-cards">
                <Row>
                  <Col md={3}>
                    <Card className="stat-card">
                      <Card.Body>
                        <h5>{stats.total}</h5>
                        <p>{t('totalMarkers')}</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="stat-card booking">
                      <Card.Body>
                        <h5>{stats.bookings}</h5>
                        <p>{t('bookings')}</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="stat-card transport">
                      <Card.Body>
                        <h5>{stats.transports}</h5>
                        <p>{t('transports')}</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="stat-card driver">
                      <Card.Body>
                        <h5>{stats.drivers}</h5>
                        <p>{t('drivers')}</p>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </div>
            </Col>
            
            <Col lg={4}>
              <Form.Group>
                <Form.Label>{t('filterBy')}</Form.Label>
                <Form.Select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  disabled={loading}
                >
                  <option value="all">{t('all')}</option>
                  <option value="booking">{t('bookings')}</option>
                  <option value="transport">{t('transports')}</option>
                  <option value="driver">{t('drivers')}</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* الخريطة */}
          <Row>
            <Col>
              <Card>
                <Card.Body className="p-0">
                  {(loading) && (
                    <div className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                      <p className="mt-2">{t('loading')}</p>
                    </div>
                  )}
                  
                  <div 
                    ref={mapRef} 
                    className="admin-map-container"
                    style={{ 
                      opacity: loading ? 0.5 : 1,
                      transition: 'opacity 0.3s ease'
                    }}
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* وسيلة الإيضاح */}
          <Row className="mt-3">
            <Col>
              <Card>
                <Card.Body>
                  <h6>{t('legend')}</h6>
                  <div className="legend-items">
                    <div className="legend-item">
                      <img 
                        src="https://img.icons8.com/arcade/25/box.png" 
                        alt="Bookings" 
                        className="legend-icon"
                      />
                      <span>{t('bookings')}</span>
                    </div>
                    <div className="legend-item">
                      <img 
                        src="https://img.icons8.com/color/35/000000/sofa.png" 
                        alt="Transports" 
                        className="legend-icon"
                      />
                      <span>{t('transports')}</span>
                    </div>
                    <div className="legend-item">
                      <img 
                        src="https://img.icons8.com/color/35/000000/truck.png" 
                        alt="Drivers" 
                        className="legend-icon"
                      />
                      <span>{t('drivers')}</span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default AdminMapView;